import React from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  IconButton, 
  Typography, 
  Box, 
  Grid, 
  Divider, 
  Paper, 
  Chip,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { format, parseISO, isValid } from 'date-fns';
import { useTranslation } from 'react-i18next';

// Banka işlemi arayüzü
interface BankTransaction {
  id: string;
  date: string;
  amount: number;
  bankAccountId: string;
  bankAccountName: string;
  bankName: string;
  bankSubAccountId?: string; // Alt hesap ID'si
  description: string;
  transactionType: string; // deposit, withdrawal, transfer
  type?: string; // income, expense - işlemin kasa/banka bakiyesine etkisi
  currency: string;
  relatedAccountId?: string;
  relatedAccountName?: string;
  documentNo?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  paymentMethod?: string; // Ödeme yöntemi: cash, credit_card, bank_transfer, check, pos
  balanceBefore?: number;
  balanceAfter?: number;
  createdByEmail?: string;
}

interface BankTransactionDetailModalProps {
  open: boolean;
  onClose: () => void;
  transaction: BankTransaction | null;
  loading?: boolean;
}

const BankTransactionDetailModal: React.FC<BankTransactionDetailModalProps> = ({
  open,
  onClose,
  transaction,
  loading = false
}) => {
  const { t, i18n } = useTranslation('accounting');
  const locale = i18n.language?.toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR';
  // Para birimi sembolünü döndür
  const getCurrencySymbol = (currency: string): string => {
    switch(currency) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'JPY': return '¥';
      case 'CHF': return 'Fr.';
      case 'AUD': return 'A$';
      case 'CAD': return 'C$';
      case 'CNY': return '¥';
      default: return currency;
    }
  };
  
  // İşlem tipine göre ikon ve renk döndür
  const getTransactionTypeInfo = (type: string) => {
    switch (type) {
      case 'deposit':
        return {
          label: t('bank.transactionReport.types.deposit'),
          icon: <AttachMoneyIcon fontSize="small" />,
          color: 'success',
          textColor: 'success.main'
        };
      case 'withdrawal':
      case 'withdraw':
        return {
          label: t('bank.transactionReport.types.withdraw'),
          icon: <MoneyOffIcon fontSize="small" />,
          color: 'error',
          textColor: 'error.main'
        };
      case 'cash_transfer':
        return {
          label: t('bank.transactionReport.types.transfer'),
          icon: <AccountBalanceWalletIcon fontSize="small" />,
          color: 'info',
          textColor: 'info.main'
        };
      default:
        return {
          label: type,
          icon: <ReceiptIcon fontSize="small" />,
          color: 'default',
          textColor: 'text.primary'
        };
    }
  };
  
  // Ödeme yöntemini görüntüle
  const getPaymentMethodName = (paymentMethod?: string): string => {
    if (!paymentMethod) return '-';
    const keyMap: Record<string, string> = {
      cash: 'cash',
      credit_card: 'creditCard',
      bank_transfer: 'bankTransfer',
      check: 'check',
      pos: 'pos',
    };
    const key = keyMap[paymentMethod];
    return key ? t(`bank.transactionReport.paymentMethods.${key}`) : paymentMethod;
  };
  
  // Firebase timestamp'lerini formatlayacak yardımcı fonksiyon
  const formatFirebaseDate = (firebaseDate: any, transactionType?: string): string => {
    if (!firebaseDate) return '-';
    
    try {
      // Eğer firebaseDate bir timestamp ise (seconds ve nanoseconds var)
      if (typeof firebaseDate === 'object' && firebaseDate.seconds && firebaseDate.nanoseconds) {
        const date = new Date(firebaseDate.seconds * 1000);
        return format(date, 'dd.MM.yyyy HH:mm');
      }
      
      // Eğer ISO string ise
      if (typeof firebaseDate === 'string') {
        try {
          let date = parseISO(firebaseDate);
          
          // QUICK_SALE işlem tipi için 3 saati düzeltme
          if (transactionType === 'QUICK_SALE') {
            date = new Date(date.getTime() - 3 * 60 * 60 * 1000);
          }
          
          if (isValid(date)) {
            return format(date, 'dd.MM.yyyy HH:mm');
          }
        } catch (e) {
          console.error('Tarih dönüştürme hatası:', e);
        }
      }
      
      // Sayı değeri ise (timestamp)
      if (typeof firebaseDate === 'number') {
        return format(new Date(firebaseDate), 'dd.MM.yyyy HH:mm');
      }
      
      return '-';
    } catch (error) {
      console.error('Tarih formatlanırken hata:', error, 'Tarih:', firebaseDate);
      return '-';
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          {t('bank.transactionReport.actions.detail')}
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white'
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 4, px: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!transaction) {
    return null;
  }

  const typeInfo = getTransactionTypeInfo(transaction.transactionType);
  const isExpense = transaction.transactionType === 'withdrawal' || 
                   transaction.transactionType === 'withdraw' ||
                   transaction.transactionType === 'odeme' || 
                   transaction.transactionType === 'payment' ||
                   transaction.amount < 0 ||
                   (transaction.transactionType === 'cash_transfer' && transaction.type === 'expense');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
        {t('bank.transactionReport.actions.detail')}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'white'
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ py: 4, px: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <Chip
              icon={typeInfo.icon}
              label={typeInfo.label}
              color={typeInfo.color as any}
              size="medium"
              sx={{ mr: 2 }}
            />
            <Box component="span" sx={{ mr: 1 }}>
              {transaction.description}
            </Box>
            {transaction.documentNo && (
              <Chip 
                label={`${t('bank.transactionReport.table.columns.documentNo')}: ${transaction.documentNo}`} 
                size="small" 
                variant="outlined" 
                sx={{ ml: 1 }} 
              />
            )}
          </Typography>
          
          <Typography variant="h6" color={isExpense ? 'error.main' : 'success.main'} fontWeight="bold">
            {isExpense ? '-' : '+'}{transaction.amount.toLocaleString(locale)} {getCurrencySymbol(transaction.currency)}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />
        
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('bank.detailModal.sections.basic')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">{t('bank.detailModal.labels.transactionDate')}:</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatFirebaseDate(transaction.date, transaction.transactionType)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">{t('bank.detailModal.labels.transactionType')}:</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {typeInfo.label}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">{t('bank.transactionReport.table.columns.paymentMethod')}:</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {getPaymentMethodName(transaction.paymentMethod)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">{t('bank.labels.currency')}:</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {transaction.currency} ({getCurrencySymbol(transaction.currency)})
                  </Typography>
                </Grid>
                {transaction.documentNo && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">{t('bank.transactionReport.table.columns.documentNo')}:</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {transaction.documentNo}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('bank.detailModal.sections.account')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">{t('bank.detailModal.labels.bank')}:</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {transaction.bankName}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">{t('bank.detailModal.labels.account')}:</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {transaction.bankAccountName}
                  </Typography>
                </Grid>
                {transaction.relatedAccountName && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">{t('bank.detailModal.labels.relatedAccount')}:</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {transaction.relatedAccountName}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
          
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('bank.detailModal.sections.balance')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                {transaction.balanceBefore !== undefined && (
                  <Grid item xs={6} md={4}>
                    <Typography variant="body2" color="text.secondary">{t('bank.detailModal.labels.balanceBefore')}:</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {transaction.balanceBefore.toLocaleString(locale)} {getCurrencySymbol(transaction.currency)}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={6} md={4}>
                  <Typography variant="body2" color="text.secondary">{t('bank.detailModal.labels.transactionAmount')}:</Typography>
                  <Typography 
                    variant="body1" 
                    fontWeight="bold"
                    color={isExpense ? 'error.main' : 'success.main'}
                  >
                    {isExpense ? '-' : '+'}{transaction.amount.toLocaleString(locale)} {getCurrencySymbol(transaction.currency)}
                  </Typography>
                </Grid>
                {transaction.balanceAfter !== undefined && (
                  <Grid item xs={6} md={4}>
                    <Typography variant="body2" color="text.secondary">{t('bank.detailModal.labels.balanceAfter')}:</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {transaction.balanceAfter.toLocaleString(locale)} {getCurrencySymbol(transaction.currency)}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
          
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('bank.detailModal.sections.system')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">{t('bank.detailModal.labels.createdAt')}:</Typography>
                  <Typography variant="body2">
                    {formatFirebaseDate(transaction.createdAt, transaction.transactionType)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">{t('bank.detailModal.labels.updatedAt')}:</Typography>
                  <Typography variant="body2">
                    {formatFirebaseDate(transaction.updatedAt, transaction.transactionType)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">{t('bank.detailModal.labels.createdBy')}:</Typography>
                  <Typography variant="body2">
                    {transaction.createdByEmail || transaction.createdBy || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">{t('bank.detailModal.labels.transactionId')}:</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {transaction.id}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default BankTransactionDetailModal; 